/**
 * REST API client for video calls
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://api.hayatfamily.app';

export interface StartCallRequest {
  device_id: string;
  senior_id: string;
}

export interface StartCallResponse {
  call_id: string;
  status: string;
  device_id: string;
  senior_id: string;
}

/**
 * Start a video call from Family App
 */
export async function startCall(deviceId: string, seniorId: string): Promise<StartCallResponse> {
  console.log('[Calls API] Starting call:', { deviceId, seniorId });
  
  const response = await fetch(`${API_BASE}/api/calls/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Send JWT cookie
    body: JSON.stringify({
      device_id: deviceId,
      senior_id: seniorId,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    console.error('[Calls API] Start call failed:', response.status, error);
    throw new Error(error.detail || `Failed to start call: ${response.status}`);
  }
  
  const data = await response.json();
  console.log('[Calls API] Call started:', data);
  return data;
}

/**
 * End an active call
 */
export async function endCall(callId: string, reason: string = 'ended'): Promise<void> {
  console.log('[Calls API] Ending call:', { callId, reason });
  
  const response = await fetch(`${API_BASE}/api/calls/${callId}/end`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ reason }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    console.error('[Calls API] End call failed:', response.status, error);
    throw new Error(error.detail || `Failed to end call: ${response.status}`);
  }
  
  console.log('[Calls API] Call ended successfully');
}

/**
 * Accept an incoming call (Device side)
 */
export async function acceptCall(callId: string, deviceId: string): Promise<void> {
  console.log('[Calls API] Accepting call:', { callId, deviceId });
  
  const response = await fetch(`${API_BASE}/api/calls/${callId}/accept`, {
    method: 'POST',
    headers: {
      'X-Device-ID': deviceId,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    console.error('[Calls API] Accept call failed:', response.status, error);
    throw new Error(error.detail || `Failed to accept call: ${response.status}`);
  }
  
  console.log('[Calls API] Call accepted successfully');
}

/**
 * Reject an incoming call (Device side)
 */
export async function rejectCall(callId: string, deviceId: string, reason: string = 'rejected'): Promise<void> {
  console.log('[Calls API] Rejecting call:', { callId, deviceId, reason });
  
  const response = await fetch(`${API_BASE}/api/calls/${callId}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Device-ID': deviceId,
    },
    body: JSON.stringify({ reason }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    console.error('[Calls API] Reject call failed:', response.status, error);
    throw new Error(error.detail || `Failed to reject call: ${response.status}`);
  }
  
  console.log('[Calls API] Call rejected successfully');
}
